import { createContext, ReactNode, useContext, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import { orderingHttpClient, platformHttpClient } from 'src/network'
import { CustomerResponse } from 'src/network/types'
import { User } from 'src/models/User'
import { useShallowMemo } from 'src/hooks/useShallowMemo'
import { webSessionRecordingService } from 'src/services/WebSessionRecordingService'
import { analyticsService } from 'src/services/AnalyticsService'
import { productAdoptionService } from 'src/services/ProductAdoptionService'
import { rollbarService } from 'src/services/RollbarService'
import { getVerbitAuthServerUrl, isUserSpeaker, redirectToLogin } from 'src/utils/env'

const IDENTIFY_QUERY_KEY = ['identify'] as const
const CUSTOMER_QUERY_KEY = ['customer'] as const

type AuthorizerProps = {
    children: ReactNode
}

export const UserProvider = (props: AuthorizerProps) => {
    const { children } = props
    const { data: user = null, isFetched: isUserFetched } = useQuery({
        queryKey: IDENTIFY_QUERY_KEY,
        queryFn: () => platformHttpClient.identify(),
        retry: false,
        refetchOnWindowFocus: false,
    })
    const { data: customerData } = useQuery({
        queryKey: CUSTOMER_QUERY_KEY,
        queryFn: () => orderingHttpClient.getCustomer(),
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    })
    const customer = customerData?.customer ?? null
    const logoutFormRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        if (!user) {
            if (isUserFetched) {
                // user may have an access to the transcript when they aren't logged in (if the transcript is public),
                // so they won't receive 401s on any transcript endpoints, and thus won't be redirected to login in the usual way;
                // however, they won't see the VC component, as we're unable to check their customer ID since they aren't logged in;
                // therefore, if user is implied to be a speaker (for instance, opening the link in email with `isSpeaker` param
                // present in the URL), we have to always force them to sign in first
                if (isUserSpeaker()) {
                    redirectToLogin()
                }

                // identify anonymously only if we're sure that we tried identifying the user
                productAdoptionService.identify()
            }

            return
        }

        productAdoptionService.identify(user.email, user)
        webSessionRecordingService.identify(user.email)
        analyticsService.identify(user.email) // TODO: use "id"
        analyticsService.updateUserProps(user)
        webSessionRecordingService.getSessionURL(analyticsService.setSessionURL.bind(analyticsService))
        rollbarService.identify(user.email)
    }, [isUserFetched, user])

    const logout = () => {
        setTimeout(() => logoutFormRef.current?.submit())
    }

    return (
        <UserContext.Provider value={useShallowMemo({ user, isUserFetched, customer })}>
            <LogoutContext.Provider value={logout}>{children}</LogoutContext.Provider>
            <form
                style={{ display: 'none' }}
                ref={logoutFormRef}
                action={`${getVerbitAuthServerUrl()}/users/sign_out?redirect_to=${encodeURIComponent(window.location.href)}`}
                method="POST">
                <input name="_method" value="delete" readOnly />
            </form>
        </UserContext.Provider>
    )
}

type UserContextValue = {
    user: User | null
    isUserFetched: boolean
    customer: CustomerResponse['customer'] | null
}

const UserContext = createContext<UserContextValue | null>(null)
const LogoutContext = createContext<(() => void) | null>(null)

export const useUserContext = () => {
    const userContextValue = useContext(UserContext)

    if (userContextValue === null) {
        throw new Error('You have forgot to use <UserContext.Provider />, shame on you.')
    }

    return userContextValue
}

export const useLogout = () => {
    const logout = useContext(LogoutContext)

    if (logout === null) {
        throw new Error('You have forgot to use <LogoutContext.Provider />, shame on you.')
    }

    return logout
}
